import { ClassData, SkillData, ClassName, SkillName, Ability } from './types';

export const CLASSES: Record<ClassName, ClassData> = {
  barbarian: { name: 'barbarian', displayName: 'Barbarian', hitDie: 12, savingThrows: ['strength', 'constitution'] },
  bard: { name: 'bard', displayName: 'Bard', hitDie: 8, savingThrows: ['dexterity', 'charisma'] },
  cleric: { name: 'cleric', displayName: 'Cleric', hitDie: 8, savingThrows: ['wisdom', 'charisma'] },
  druid: { name: 'druid', displayName: 'Druid', hitDie: 8, savingThrows: ['intelligence', 'wisdom'] },
  fighter: { name: 'fighter', displayName: 'Fighter', hitDie: 10, savingThrows: ['strength', 'constitution'] },
  monk: { name: 'monk', displayName: 'Monk', hitDie: 8, savingThrows: ['strength', 'dexterity'] },
  paladin: { name: 'paladin', displayName: 'Paladin', hitDie: 10, savingThrows: ['wisdom', 'charisma'] },
  ranger: { name: 'ranger', displayName: 'Ranger', hitDie: 10, savingThrows: ['strength', 'dexterity'] },
  rogue: { name: 'rogue', displayName: 'Rogue', hitDie: 8, savingThrows: ['dexterity', 'intelligence'] },
  sorcerer: { name: 'sorcerer', displayName: 'Sorcerer', hitDie: 6, savingThrows: ['constitution', 'charisma'] },
  warlock: { name: 'warlock', displayName: 'Warlock', hitDie: 8, savingThrows: ['wisdom', 'charisma'] },
  wizard: { name: 'wizard', displayName: 'Wizard', hitDie: 6, savingThrows: ['intelligence', 'wisdom'] },
};

export const SKILLS: Record<SkillName, SkillData> = {
  acrobatics: { name: 'acrobatics', displayName: 'Acrobatics', ability: 'dexterity' },
  animal_handling: { name: 'animal_handling', displayName: 'Animal Handling', ability: 'wisdom' },
  arcana: { name: 'arcana', displayName: 'Arcana', ability: 'intelligence' },
  athletics: { name: 'athletics', displayName: 'Athletics', ability: 'strength' },
  deception: { name: 'deception', displayName: 'Deception', ability: 'charisma' },
  history: { name: 'history', displayName: 'History', ability: 'intelligence' },
  insight: { name: 'insight', displayName: 'Insight', ability: 'wisdom' },
  intimidation: { name: 'intimidation', displayName: 'Intimidation', ability: 'charisma' },
  investigation: { name: 'investigation', displayName: 'Investigation', ability: 'intelligence' },
  medicine: { name: 'medicine', displayName: 'Medicine', ability: 'wisdom' },
  nature: { name: 'nature', displayName: 'Nature', ability: 'intelligence' },
  perception: { name: 'perception', displayName: 'Perception', ability: 'wisdom' },
  performance: { name: 'performance', displayName: 'Performance', ability: 'charisma' },
  persuasion: { name: 'persuasion', displayName: 'Persuasion', ability: 'charisma' },
  religion: { name: 'religion', displayName: 'Religion', ability: 'intelligence' },
  sleight_of_hand: { name: 'sleight_of_hand', displayName: 'Sleight of Hand', ability: 'dexterity' },
  stealth: { name: 'stealth', displayName: 'Stealth', ability: 'dexterity' },
  survival: { name: 'survival', displayName: 'Survival', ability: 'wisdom' },
};

export const ABILITY_NAMES: Record<Ability, string> = {
  strength: 'Strength',
  dexterity: 'Dexterity',
  constitution: 'Constitution',
  intelligence: 'Intelligence',
  wisdom: 'Wisdom',
  charisma: 'Charisma',
};

// WHY: Canonical ability order (STR→CHA) is the single source of truth for
// grouping skills on the radar. Defines both the Skills-radar grouping and the
// Saving Throws axis order so the two charts stay consistent.
export const ABILITY_ORDER: Ability[] = [
  'strength',
  'dexterity',
  'constitution',
  'intelligence',
  'wisdom',
  'charisma',
];

// WHY: Skills radar groups skills by ability then alphabetizes within each
// group. Derived from SKILLS so a newly added skill lands in the right place
// automatically instead of relying on object-insertion order.
// RULE: PHB p.178 — every skill is tied to one of the six abilities, so
// clustering by ability mirrors how the rulebook presents them.
export const SKILLS_ORDERED: SkillName[] = ABILITY_ORDER.flatMap((ability) =>
  (Object.keys(SKILLS) as SkillName[])
    .filter((key) => SKILLS[key].ability === ability)
    .sort((a, b) => SKILLS[a].displayName.localeCompare(SKILLS[b].displayName)),
);

// WHY: Soft, mutually-distinguishable pastel palette keyed by ability. Used to
// tint both the radar point dots and axis labels so each skill/throw reads as
// its backing ability at a glance. Tuned for the dark theme's near-black navy
// surface (light-on-dark, legible without being neon).
export const ABILITY_PASTELS: Record<Ability, string> = {
  strength: '#f1a8a8',
  dexterity: '#a8e0b8',
  constitution: '#f5d6a0',
  intelligence: '#a8c4f1',
  wisdom: '#c5a8f1',
  charisma: '#f1a8d6',
};

// WHY: Darker, saturated twin of ABILITY_PASTELS for the LIGHT theme. The dark
// pastels are near-invisible on the near-white surface, so these 600-weight
// shades keep each ability's hue readable on light backgrounds.
export const ABILITY_PASTELS_LIGHT: Record<Ability, string> = {
  strength: '#dc2626',
  dexterity: '#059669',
  constitution: '#d97706',
  intelligence: '#2563eb',
  wisdom: '#7c3aed',
  charisma: '#db2777',
};

export const DEFAULT_SKILLS: Record<SkillName, 'none'> = {
  acrobatics: 'none',
  animal_handling: 'none',
  arcana: 'none',
  athletics: 'none',
  deception: 'none',
  history: 'none',
  insight: 'none',
  intimidation: 'none',
  investigation: 'none',
  medicine: 'none',
  nature: 'none',
  perception: 'none',
  performance: 'none',
  persuasion: 'none',
  religion: 'none',
  sleight_of_hand: 'none',
  stealth: 'none',
  survival: 'none',
};

// WHY: Canonical PHB language list (PHB p.123) powering typeahead suggestions on
// the character form. Free-text custom entries are still allowed — this is only the
// suggestion source, not a validation whitelist. Standard vs Exotic split mirrors
// the sourcebook's own grouping.
export const LANGUAGES: string[] = [
  'Common',
  // Standard languages
  'Dwarvish',
  'Elvish',
  'Giant',
  'Gnomish',
  'Goblin',
  'Halfling',
  'Orc',
  // Exotic languages
  'Abyssal',
  'Celestial',
  'Draconic',
  'Deep Speech',
  'Infernal',
  'Primordial',
  'Sylvan',
  'Undercommon',
];

// WHY: PHB tool proficiency suggestions (PHB p.154-157). Covers artisan's tools,
// musical instruments, kits, and — per the user request for "games like dice and
// playing cards from the game table" — the gaming sets (Dice set, Dragonchess set,
// Playing card set, Three-Dragon Ante set). Custom entries remain allowed.
export const TOOL_PROFICIENCIES: string[] = [
  // Artisan's tools
  "Alchemist's supplies",
  "Brewer's supplies",
  "Calligrapher's supplies",
  "Carpenter's tools",
  "Cartographer's tools",
  "Cobbler's tools",
  "Cook's utensils",
  "Glassblower's tools",
  "Jeweler's tools",
  "Leatherworker's tools",
  "Mason's tools",
  "Painter's supplies",
  "Potter's tools",
  "Smith's tools",
  "Tinker's tools",
  "Weaver's tools",
  "Woodcarver's tools",
  // Gaming sets
  'Dice set',
  'Dragonchess set',
  'Playing card set',
  'Three-Dragon Ante set',
  // Musical instruments
  'Bagpipes',
  'Drum',
  'Dulcimer',
  'Flute',
  'Lute',
  'Lyre',
  'Horn',
  'Pan flute',
  'Shawm',
  'Viol',
  // Kits
  'Disguise kit',
  'Forgery kit',
  'Herbalism kit',
  "Navigator's tools",
  "Poisoner's kit",
  "Thieves' tools",
  'Vehicles',
];
